// Stimulus controller for workflow diagram visualization (Issue #1297)
// Uses Cytoscape.js with dagre layout for directed graph rendering
import { Controller } from "@hotwired/stimulus"
import cytoscape from "cytoscape"
import dagre from "cytoscape-dagre"

// Register dagre layout
cytoscape.use(dagre)

export default class extends Controller {
  static values = {
    states: { type: Array, default: [] },
    transitions: { type: Array, default: [] }
  }

  connect() {
    this.renderDiagram()
  }

  disconnect() {
    if (this.cy) {
      this.cy.destroy()
    }
  }

  renderDiagram() {
    // Clear placeholder text
    this.element.innerHTML = ""

    // Build nodes from states
    const nodes = this.statesValue.map(state => ({
      data: {
        id: `state-${state.id}`,
        label: state.name,
        color: state.color || "#6c757d",
        isInitial: state.is_initial,
        isTerminal: state.is_terminal
      }
    }))

    // Build edges from transitions
    const edges = this.transitionsValue.map(transition => ({
      data: {
        id: `edge-${transition.id}`,
        source: `state-${transition.from_state_id}`,
        target: `state-${transition.to_state_id}`
      }
    }))

    // Initialize Cytoscape
    this.cy = cytoscape({
      container: this.element,
      elements: { nodes, edges },
      style: [
        {
          selector: "node",
          style: {
            "label": "data(label)",
            "text-valign": "center",
            "text-halign": "center",
            "background-color": "data(color)",
            "color": "#fff",
            "text-outline-color": "#000",
            "text-outline-width": 1,
            "font-size": "12px",
            "font-weight": "bold",
            "width": 120,
            "height": 40,
            "shape": "roundrectangle",
            "border-width": 2,
            "border-color": "#495057"
          }
        },
        {
          selector: "node[?isInitial]",
          style: {
            "border-color": "#198754",
            "border-width": 4
          }
        },
        {
          selector: "node[?isTerminal]",
          style: {
            "border-color": "#dc3545",
            "border-width": 4
          }
        },
        {
          selector: "edge",
          style: {
            "width": 2,
            "line-color": "#6c757d",
            "target-arrow-color": "#6c757d",
            "target-arrow-shape": "triangle",
            "curve-style": "bezier",
            "arrow-scale": 1.2
          }
        }
      ],
      layout: {
        name: "dagre",
        rankDir: "LR",
        nodeSep: 50,
        rankSep: 80,
        padding: 20
      },
      userZoomingEnabled: true,
      userPanningEnabled: true,
      boxSelectionEnabled: false
    })

    // Fit diagram to container
    this.cy.fit(20)

    // Show message if no states
    if (this.statesValue.length === 0) {
      this.element.innerHTML = '<p class="text-muted text-center pt-5">No states defined. Add states to see the workflow diagram.</p>'
    }
  }

  // Re-render when values change (for Turbo updates)
  statesValueChanged() {
    if (this.cy) {
      this.renderDiagram()
    }
  }

  transitionsValueChanged() {
    if (this.cy) {
      this.renderDiagram()
    }
  }
}
